import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { DynamicLogo } from "../DynamicLogo";
import { X, Menu } from "lucide-react";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Serviços", to: "/servicos" },
  { label: "Produtos", to: "/produtos" },
  { label: "PMOC", to: "/pmoc" },
  { label: "Contato", to: "/contato" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 100);
    };

    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, []);

  // Fecha menu mobile ao redimensionar para desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Previne scroll quando menu mobile está aberto
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1], delay: 0.2 }}
        className={`fixed top-0 z-50 w-full transition-all duration-500 ease-out ${
          isScrolled 
            ? 'bg-slate-900/95 backdrop-blur-xl border-b border-cyan-400/20 shadow-2xl shadow-black/30' 
            : 'bg-gradient-to-b from-black/20 via-black/10 to-transparent border-b border-transparent'
        }`}
      >
        <div className={`container flex items-center justify-between transition-all duration-500 ${
          isScrolled ? 'h-16 lg:h-18' : 'h-20 lg:h-24'
        }`}>
          <Link to="/" className="flex items-center group" onClick={() => setIsMobileMenuOpen(false)}>
            <DynamicLogo 
              alt="Arcgel - Soluções em Climatização" 
              className={`h-10 w-auto transition-all duration-300 group-hover:scale-105 ${
                isScrolled ? 'drop-shadow-lg' : 'drop-shadow-2xl'
              }`}
              fallback={
                <div className="h-10 w-24 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                  ARCGEL
                </div>
              }
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="flex-1">
            <ul className="hidden md:flex gap-4 lg:gap-6 justify-end items-center">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`relative px-3 lg:px-4 py-2 font-medium text-sm lg:text-base transition-all duration-300 rounded-lg ${
                      isScrolled 
                        ? 'text-white/90 hover:text-white hover:bg-cyan-500/20' 
                        : 'text-white/95 hover:text-white hover:bg-white/10'
                    } hover:shadow-lg focus-visible:ring-2 focus-visible:ring-cyan-400`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/area-cliente"
                  className={`ml-2 px-4 lg:px-6 py-2 lg:py-2.5 rounded-full font-semibold text-xs lg:text-sm transition-all duration-300 inline-block ${
                    isScrolled 
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg hover:shadow-xl hover:shadow-cyan-500/25 hover:scale-105' 
                      : 'bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 hover:border-white/40'
                  }`}
                >
                  Área do Cliente
                </Link>
              </li>
            </ul>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center justify-end">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isScrolled 
                    ? 'text-white hover:bg-cyan-500/20' 
                    : 'text-white hover:bg-white/10'
                }`}
                aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="absolute right-0 top-0 h-full w-72 bg-slate-900/98 backdrop-blur-xl border-l border-cyan-400/20 shadow-2xl"
            >
              <div className="flex flex-col h-full pt-24 pb-8 px-6">
                <ul className="flex flex-col gap-2">
                  {navItems.map((item, index) => (
                    <motion.li
                      key={item.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.to}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-4 py-3 text-lg font-medium text-white/90 hover:text-white hover:bg-cyan-500/20 rounded-lg transition-all duration-300"
                      >
                        {item.label}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
                
                <div className="mt-auto">
                  <Link
                    to="/area-cliente"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full px-6 py-3 text-center font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full shadow-lg hover:shadow-cyan-500/25 transition-all duration-300"
                  >
                    Área do Cliente
                  </Link>
                  
                  <a
                    href="https://wa.me/5513991028221"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full mt-3 px-6 py-3 text-center font-semibold text-white bg-green-600 rounded-full shadow-lg hover:bg-green-500 transition-all duration-300"
                  >
                    WhatsApp
                  </a>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
